/*
 * IconListTabs  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { ITabsProps, Tabs, TABS_STYLE } from "../Tabs";
import { ITabbedContainerProps, TABBED_CONTAINER_TYPE, TabbedContainer } from "../TabbedContainer";
import { IconListGrid } from "./IconListGrid";
import { useDeepCompareMemo } from "use-deep-compare";
import { IIconListPack } from "./IconListTypes";
import { useIconListContext } from "./IconListContext";
import { IconListSearch } from "./IconListSearch";

export interface IIconListTabs {
	className?: string;
	iconPacks: IIconListPack[];
}

const allTab = {
	id: "all",
	label: "All",
	tooltip: "All"
};

export const IconListTabs: React.FC<IIconListTabs> = ({ className, iconPacks }) => {
	const { selectedPackName } = useIconListContext();
	const baseClass = BEM("edt-icon-list-tabs");

	const allTabContainerItem = useDeepCompareMemo(
		() => ({
			id: allTab.id,
			render: () => (
				<div className={baseClass.element("tab-content").toClassName()}>
					<IconListGrid iconPacks={iconPacks} />
				</div>
			)
		}),
		[ iconPacks ]
	);

	const { tabs, tabContainerItems } = useDeepCompareMemo<{
		tabs: ITabsProps["items"];
		tabContainerItems: ITabbedContainerProps["items"];
	}>(
		() =>
			iconPacks.reduce(
				(acc, iconPack) => {
					const id = iconPack.name;

					const tab = {
						id,
						label: iconPack.shortLabel,
						tooltip: iconPack.label
					};

					const tabContainerItem = {
						id,
						render: () => (
							<div className={baseClass.element("tab-content").toClassName()}>
								<IconListGrid iconPacks={[ iconPack ]} />
							</div>
						)
					};

					return {
						...acc,
						tabs: [ ...acc.tabs, tab ],
						tabContainerItems: [ ...acc.tabContainerItems, tabContainerItem ]
					};
				},
				{ tabs: [ allTab ], tabContainerItems: [ allTabContainerItem ] }
			),
		[ iconPacks ]
	);

	const [ activeTab, setActiveTab ] = useState<typeof tabs[number]["id"]>(selectedPackName || tabs[0]?.id);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<Tabs
				className={baseClass.element("tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.COMPACT}
			/>
			<IconListSearch />
			<TabbedContainer
				className={baseClass.element("tab-container").toClassName()}
				style={TABBED_CONTAINER_TYPE.SLIDE}
				items={tabContainerItems}
				selectedId={activeTab}
			/>
		</div>
	);
};
